import {
  BooleanField,
  BooleanInput,
  Button,
  Edit,
  ReferenceInput,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  useDataProvider,
  useEditController,
  useNotify,
  usePermissions,
  useRedirect,
  useRefresh,
  FormDataConsumer,
} from 'react-admin'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import userPermissions from './userPermissions'
import { getLabelSuffix } from '../helpers'
import PasswordEdit from '../Profiles/PasswordEdit'

const EditToolbar = () => {
  return (
    <Toolbar>
      <SaveButton />
    </Toolbar>
  )
}

const UserEdit = () => {
  const { id } = useParams()
  const { t } = useTranslation()
  const suffix = getLabelSuffix()
  const notify = useNotify()
  const refresh = useRefresh()
  const redirect = useRedirect()
  const dataProvider = useDataProvider()
  const { record, isLoading } = useEditController({
    resource: 'users',
    id,
  })
  const { permissions } = usePermissions()

  const handleReset2FA = async () => {
    if (
      window.confirm(
        'Are you sure you want to reset 2FA for this user? They will need to set it up again.'
      )
    ) {
      try {
        await dataProvider.custom(`users/${id}/reset-2fa`, {
          method: 'POST',
        })
        notify('2FA has been reset for this user', { type: 'success' })
        refresh()
      } catch (error) {
        console.error('Error resetting 2FA:', error)
        notify('Error resetting 2FA', { type: 'error' })
      }
    }
  }

  const transform = (values) => {
    let perm = 0

    for (const p in values._perm) {
      if (values._perm[p] === true) {
        if (userPermissions[p]) {
          perm += userPermissions[p]
        } else {
          console.log(p)
        }
      }
    }

    values.permission_flags = perm
    return values
  }

  if (isLoading) {
    return null
  }

  // convert flags
  record._perm = []

  for (const p in userPermissions) {
    if (record.permission_flags & userPermissions[p]) {
      record._perm[p] = true
    } else {
      record._perm[p] = false
    }
  }

  const onSuccess = async (data) => {
    console.log(data)
    // const details = {
    //   fullName: data.name,
    //   ...data,
    // }
    // localStorage.setItem('user', JSON.stringify(details))
    notify(`Changes saved`)
    redirect('/admin/users')
    refresh()
  }

  return (
    <section className="space-y-12">
      <div className="space-y-10">
        <div>
          <Edit
            mutationOptions={{ onSuccess }}
            transform={transform}
            mutationMode="pessimistic"
          >
            <SimpleForm record={record} toolbar={<EditToolbar />}>
              {permissions & userPermissions.SUPER_ADMIN ? (
                <ReferenceInput
                  source="organisation_id"
                  reference="organisations"
                >
                  <SelectInput
                    label={t('organisation')}
                    optionText="name"
                    optionValue="id"
                  />
                </ReferenceInput>
              ) : null}
              <TextInput label={t('name')} source="name" />
              <TextInput label={t('email')} source="email" />
              <TextInput
                label="Email Send As"
                source="email_send_as"
                helperText="If set, this email will be used as the sender for questionnaires. Leave empty to use the default system email."
              />

              {permissions & userPermissions.SUPER_ADMIN ? (
                <>
                  <h3>Two-Factor Authentication</h3>
                  <div className="flex items-center space-x-4 mb-4">
                    <BooleanInput label="2FA Enabled" source="is_2fa_enabled" />
                    <FormDataConsumer>
                      {({ formData }) =>
                        formData?.is_2fa_enabled ? (
                          <Button
                            label="Reset 2FA"
                            onClick={handleReset2FA}
                            variant="contained"
                            color="secondary"
                            size="small"
                          />
                        ) : null
                      }
                    </FormDataConsumer>
                  </div>
                </>
              ) : (permissions & userPermissions.CREATE_USERS ||
                  permissions & userPermissions.EDIT_USERS) &&
                record?.is_2fa_enabled ? (
                <>
                  <h3>Two-Factor Authentication</h3>
                  <div className="flex items-center space-x-4 mb-4">
                    <Button
                      label="Reset 2FA"
                      onClick={handleReset2FA}
                      variant="contained"
                      color="secondary"
                      size="small"
                    />
                  </div>
                </>
              ) : null}

              <h3>{t('permissions')}</h3>
              <h4>{t('login')}</h4>
              <span>{t('can_login')}</span>
              <BooleanInput label="Can login" source="_perm[LOGIN]" />
              <h4>{t('users')}</h4>
              <span>{t('view_users')}</span>
              <BooleanInput label="Can view" source="_perm[VIEW_USERS]" />
              <span>{t('create_users')}</span>
              <BooleanInput label="Can create" source="_perm[CREATE_USERS]" />
              <span>{t('edit_users')}</span>
              <BooleanInput label="Can edit" source="_perm[EDIT_USERS]" />
              <h4>
                {t('data')} / {t(`transaction${suffix}`)} /{' '}
                {t(`vendors${suffix}`)}
              </h4>
              <span>{t('view_transactions')}</span>
              <BooleanInput
                label="Can view"
                source="_perm[VIEW_TRANSACTIONS]"
              />
              <span>{t('create_transactions')}</span>
              <BooleanInput
                label="Can create"
                source="_perm[CREATE_TRANSACTIONS]"
              />
              <span>{t(`edit_transactions${suffix}`)}</span>
              <BooleanInput
                label="Can edit"
                source="_perm[EDIT_TRANSACTIONS]"
              />
              <span>{t(`delete_transactions${suffix}`)}</span>
              <BooleanInput
                label="Can delete"
                source="_perm[DELETE_TRANSACTIONS]"
              />
              <h4>{t('tag_categories')}</h4>
              <span>{t('view_tag_categories')}</span>
              <BooleanInput
                label="Can view"
                source="_perm[VIEW_TAG_CATEGORIES]"
              />
              <span>{t('create_tag_categories')}</span>
              <BooleanInput
                label="Can create"
                source="_perm[CREATE_TAG_CATEGORIES]"
              />
              <span>{t(`edit_tag_categories${suffix}`)}</span>
              <BooleanInput
                label="Can edit"
                source="_perm[EDIT_TAG_CATEGORIES]"
              />

              {permissions & userPermissions.SUPER_ADMIN ? (
                <>
                  <h4>{t('superuser')}</h4>
                  <BooleanInput
                    label={t('superuser_permissions')}
                    source="_perm[SUPER_ADMIN]"
                  />
                </>
              ) : null}
            </SimpleForm>
          </Edit>
        </div>
        {permissions & userPermissions.SUPER_ADMIN ? (
          <div className="space-y-4">
            <h2 className="font-semibold">{t('edit_password')}</h2>
            <PasswordEdit userId={id} />
          </div>
        ) : null}
      </div>
    </section>
  )
}

export default UserEdit
